/**
 * CalendarPage — Module Calendrier SuiviPlus
 * FullCalendar (vue mois/semaine/jour) + CRUD événements + catégories
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Calendar, Plus, X, Lock, Globe, Loader, Trash2, Link2, Copy, CheckSquare, Check, ExternalLink, Users } from 'lucide-react';
import {
  getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent,
  getCalendarCategories, getCalendarTasks, updateTask, createCalendarException,
  completeTaskOccurrence, getEmployees, getEmployeePlanningEntries,
} from '../api';
import { useStructure } from '../contexts/StructureContext';
import { useToast } from '../contexts/NotificationContext';
import Modal from '../components/ui/Modal';
import LoadingSpinner from '../components/LoadingSpinner';

// Retirer 1 jour (pour convertir endDate exclusive FullCalendar → inclusive)
const subtractDay = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  dt.setDate(dt.getDate() - 1);
  return toLocalDate(dt);
};

// Convertir une date en format datetime-local (timezone local, pas UTC)
const toLocalDatetime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const offset = dt.getTimezoneOffset();
  const local = new Date(dt.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

const toLocalDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const offset = dt.getTimezoneOffset();
  const local = new Date(dt.getTime() - offset * 60000);
  return local.toISOString().slice(0, 10);
};

// Contenu du panneau Planning équipe — affiché dans un drawer accessible via bouton header.
const EmployeePanelContent = ({ employees, employeeColors, checkedEmployeeIds, toggleEmployee }) => {
  if (employees.length === 0) {
    return <p className="text-xs text-gray-400">Aucun employé actif</p>;
  }
  return (
    <div className="space-y-2">
      {employees.map((emp) => {
        const color = employeeColors[emp.id];
        const checked = checkedEmployeeIds.has(emp.id);
        return (
          <label key={emp.id} htmlFor={`emp-check-${emp.id}`} className="flex items-center gap-2 cursor-pointer group select-none">
            <input
              id={`emp-check-${emp.id}`}
              type="checkbox"
              checked={checked}
              onChange={() => toggleEmployee(emp)}
              className="rounded border-gray-300 cursor-pointer"
              style={checked ? { accentColor: color } : {}}
            />
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 transition-opacity" style={{ backgroundColor: color, opacity: checked ? 1 : 0.35 }} />
            <span className="text-xs text-gray-700 truncate group-hover:text-gray-900">{emp.firstName} {emp.lastName}</span>
          </label>
        );
      })}
    </div>
  );
};

const EMPLOYEE_COLORS = [
  '#14b8a6', '#8b5cf6', '#f97316', '#06b6d4',
  '#ec4899', '#84cc16', '#f59e0b', '#3b82f6',
];

function getCycleWeek(date, cycleStartWeek, referenceDate, nbWeeks) {
  const ref = new Date(referenceDate); ref.setHours(0, 0, 0, 0);
  const d = new Date(date); d.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((d - ref) / 864e5);
  const weekOffset = Math.floor(diffDays / 7);
  return ((cycleStartWeek - 1 + weekOffset) % nbWeeks + nbWeeks) % nbWeeks + 1;
}

function buildPlanningEvents(emp, schedules, anchor, color, rangeStart, rangeEnd) {
  if (!schedules || !schedules.length) return [];
  const byWeek = {};
  for (const s of schedules) {
    if (!byWeek[s.weekNumber]) byWeek[s.weekNumber] = {};
    byWeek[s.weekNumber][s.dayOfWeek] = s;
  }
  const nbWeeks = Math.max(...schedules.map(s => s.weekNumber));
  const refDate = anchor?.date || new Date();
  const refCycle = anchor?.cycleStartWeek || 1;
  const events = [];
  const cursor = new Date(rangeStart); cursor.setHours(0, 0, 0, 0);
  const endDate = new Date(rangeEnd);
  while (cursor < endDate) {
    const dow = cursor.getDay();
    const dayOfWeek = dow === 0 ? 7 : dow;
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      const planW = getCycleWeek(cursor, refCycle, refDate, nbWeeks);
      const slot = byWeek[planW]?.[dayOfWeek];
      if (slot) {
        const vf = new Date(slot.validFrom);
        const vu = slot.validUntil ? new Date(slot.validUntil) : null;
        if (cursor >= vf && (!vu || cursor <= vu)) {
          const dateStr = cursor.toISOString().slice(0, 10);
          const [sh, sm] = slot.startTime.split(':').map(Number);
          const [eh, em] = slot.endTime.split(':').map(Number);
          const startDt = new Date(cursor); startDt.setHours(sh, sm, 0, 0);
          const endDt = new Date(cursor); endDt.setHours(eh, em, 0, 0);
          events.push({
            id: `planning-${emp.id}-${dateStr}`,
            title: `${emp.firstName} ${emp.lastName}`,
            start: startDt.toISOString(),
            end: endDt.toISOString(),
            backgroundColor: color + '33',
            borderColor: color,
            textColor: '#1e293b',
            extendedProps: { type: 'planning', employeeId: emp.id },
            editable: false,
          });
        }
      }
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return events;
}

const EMPTY_EVENT = {
  title: '', description: '', location: '',
  startAt: '', endAt: '', allDay: false,
  isPrivate: false, categoryId: '', structureId: '', recurrence: '', recurrenceEndDate: '',
};

const CalendarPage = ({ user, showAllStructures = false }) => {
  const { currentStructure, structures } = useStructure();
  const toast = useToast();
  const calendarRef = useRef(null);

  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_EVENT });
  const [saving, setSaving] = useState(false);
  const [taskPopup, setTaskPopup] = useState(null); // { task, x, y }
  // Drawer mobile pour le panneau Planning équipe (sous lg uniquement)
  const [showEmployeePanel, setShowEmployeePanel] = useState(false);
  const [exceptionInfo, setExceptionInfo] = useState(null); // { originalId, excludeDate }

  // Planning équipe
  const [employees, setEmployees] = useState([]);
  const [employeeColors, setEmployeeColors] = useState({});
  const [employeeData, setEmployeeData] = useState({});
  const [checkedEmployeeIds, setCheckedEmployeeIds] = useState(new Set());
  const [planningEvents, setPlanningEvents] = useState([]);
  const [calendarRange, setCalendarRange] = useState(null);

  const loadCategories = useCallback(async () => {
    try {
      const res = await getCalendarCategories();
      setCategories(res.data || []);
    } catch {}
  }, []);

  const loadEvents = useCallback(async (fetchInfo) => {
    setLoading(true);
    try {
      const params = {};
      if (fetchInfo?.startStr) params.start = fetchInfo.startStr;
      if (fetchInfo?.endStr) params.end = fetchInfo.endStr;
      if (!showAllStructures && currentStructure?.id) params.structureId = currentStructure.id;

      // Charger événements + tâches en parallèle
      const [eventsRes, tasksRes] = await Promise.all([
        getCalendarEvents(params),
        getCalendarTasks(params),
      ]);

      const calEvents = (eventsRes.data || []).map(e => ({
        id: e.id,
        title: e.title,
        start: e.startAt,
        end: e.endAt || undefined,
        allDay: e.allDay,
        backgroundColor: e.category?.color || '#6366f1',
        borderColor: e.category?.color || '#6366f1',
        textColor: '#1e293b',
        extendedProps: { ...e, type: 'event' },
        classNames: e.isPrivate ? ['opacity-70'] : [],
      }));

      const calTasks = (tasksRes.data || []).map(t => {
        const isCompleted = t.status === 'terminé';
        let start = t.dueDate;
        if (t.dueTime && t.dueDate) {
          // Combiner date + heure
          const d = new Date(t.dueDate);
          const [h, m] = t.dueTime.split(':').map(Number);
          d.setHours(h, m, 0);
          start = d.toISOString();
        }
        // Fin = 30 min après le début si heure précisée
        let end = undefined;
        if (t.dueTime && start) {
          end = new Date(new Date(start).getTime() + 30 * 60000).toISOString();
        }
        return {
          id: `task_${t.id}`,
          title: `${isCompleted ? '✓ ' : '☐ '}${t.title}`,
          start,
          end,
          allDay: !t.dueTime,
          backgroundColor: isCompleted ? '#d1d5db' : '#fde68a',
          borderColor: isCompleted ? '#d1d5db' : '#fde68a',
          textColor: '#1e293b',
          extendedProps: { ...t, type: 'task' },
          classNames: isCompleted ? ['line-through', 'opacity-60'] : [],
        };
      });

      setEvents([...calEvents, ...calTasks]);
    } catch {
      toast.error('Erreur chargement');
    }
    setLoading(false);
  }, [currentStructure?.id, showAllStructures, toast]);

  const loadEmployees = useCallback(async () => {
    if (!currentStructure?.id) return;
    try {
      const res = await getEmployees({ structureId: currentStructure.id, status: 'active' });
      const emps = res.data || [];
      setEmployees(emps);
      const colors = {};
      emps.forEach((e, i) => { colors[e.id] = EMPLOYEE_COLORS[i % EMPLOYEE_COLORS.length]; });
      setEmployeeColors(colors);
      setCheckedEmployeeIds(new Set());
      setEmployeeData({});
      setPlanningEvents([]);
    } catch {}
  }, [currentStructure?.id]);

  const toggleEmployee = async (emp) => {
    const empId = emp.id;
    const isChecked = checkedEmployeeIds.has(empId);
    if (isChecked) {
      setCheckedEmployeeIds(prev => { const next = new Set(prev); next.delete(empId); return next; });
    } else {
      let data = employeeData[empId];
      if (!data) {
        try {
          const res = await getEmployeePlanningEntries(empId);
          data = res.data;
          setEmployeeData(prev => ({ ...prev, [empId]: data }));
        } catch {
          toast.error('Erreur chargement planning');
          return;
        }
      }
      setCheckedEmployeeIds(prev => { const next = new Set(prev); next.add(empId); return next; });
    }
  };

  // Regénérer les événements planning quand les employés cochés ou la plage changent
  useEffect(() => {
    if (!calendarRange) { setPlanningEvents([]); return; }
    const evts = [];
    for (const empId of checkedEmployeeIds) {
      const emp = employees.find(e => e.id === empId);
      const data = employeeData[empId];
      const color = employeeColors[empId];
      if (!emp || !data || !color) continue;
      evts.push(...buildPlanningEvents(emp, data.schedules, data.anchor, color, calendarRange.start, calendarRange.end));
    }
    setPlanningEvents(evts);
  }, [checkedEmployeeIds, employeeData, employees, employeeColors, calendarRange]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  // Clic sur un créneau vide → créer
  const handleDateSelect = (selectInfo) => {
    setEditingEvent(null);
    const isAllDay = selectInfo.allDay;
    setForm({
      ...EMPTY_EVENT,
      startAt: isAllDay ? toLocalDate(selectInfo.start) : toLocalDatetime(selectInfo.start),
      endAt: isAllDay ? toLocalDate(selectInfo.end) : toLocalDatetime(selectInfo.end),
      allDay: isAllDay,
      structureId: '',
    });
    setShowModal(true);
  };

  // Clic sur un événement → éditer (ou info tâche)
  const handleEventClick = async (clickInfo) => {
    const raw = clickInfo.event.extendedProps;
    if (raw.type === 'planning') return; // Non interactif
    if (raw.type === 'task') {
      const rect = clickInfo.el.getBoundingClientRect();
      setTaskPopup({ task: raw, x: rect.left, y: rect.bottom + 5 });
      return;
    }

    // Si événement récurrent, demander quoi faire
    if (raw.recurrence && raw._originalId) {
      const choice = prompt(
        'Cet événement est récurrent :\n\n1 = Modifier toute la série\n2 = Modifier cette occurrence uniquement\n3 = Supprimer cette occurrence\n\nTapez 1, 2 ou 3'
      );
      if (!choice) return;

      if (choice === '3') {
        // Supprimer cette occurrence uniquement → exclure la date sans créer d'exception
        const occDate = new Date(raw.startAt);
        const excludeDate = `${occDate.getFullYear()}-${String(occDate.getMonth() + 1).padStart(2, '0')}-${String(occDate.getDate()).padStart(2, '0')}`;
        try {
          await createCalendarException(raw._originalId, { excludeDate, excludeOnly: true });
          toast.success('Occurrence supprimée');
          loadEvents();
        } catch { toast.error('Erreur suppression'); }
        return;
      }

      if (choice === '2') {
        // Exception pour cette occurrence → modifier et exclure du récurrent
        const occDate = new Date(raw.startAt);
        const excludeDate = `${occDate.getFullYear()}-${String(occDate.getMonth() + 1).padStart(2, '0')}-${String(occDate.getDate()).padStart(2, '0')}`;
        setExceptionInfo({ originalId: raw._originalId, excludeDate });
        setEditingEvent(null);
        const isAllDayExc = raw.allDay || false;
        setForm({
          title: raw.title || '',
          description: raw.description || '',
          location: raw.location || '',
          startAt: isAllDayExc ? toLocalDate(raw.startAt) : toLocalDatetime(raw.startAt),
          endAt: raw.endAt ? (isAllDayExc ? subtractDay(raw.endAt) : toLocalDatetime(raw.endAt)) : '',
          allDay: raw.allDay || false,
          isPrivate: raw.isPrivate || false,
          categoryId: raw.categoryId || '',
          structureId: raw.structureId || '',
          recurrence: '',
        });
        setShowModal(true);
        return;
      }
      if (choice !== '1') return;
      // Modifier tous → utiliser l'ID source
      const sourceEvent = { ...raw, id: raw._originalId };
      const isAllDaySrc = sourceEvent.allDay || false;
      setEditingEvent(sourceEvent);
      setForm({
        title: sourceEvent.title || '',
        description: sourceEvent.description || '',
        location: sourceEvent.location || '',
        startAt: sourceEvent.startAt ? (isAllDaySrc ? toLocalDate(sourceEvent.startAt) : toLocalDatetime(sourceEvent.startAt)) : '',
        endAt: sourceEvent.endAt ? (isAllDaySrc ? subtractDay(sourceEvent.endAt) : toLocalDatetime(sourceEvent.endAt)) : '',
        allDay: sourceEvent.allDay || false,
        isPrivate: sourceEvent.isPrivate || false,
        categoryId: sourceEvent.categoryId || '',
        structureId: sourceEvent.structureId || '',
        recurrence: sourceEvent.recurrence || '',
        recurrenceEndDate: sourceEvent.recurrenceEndDate ? toLocalDate(sourceEvent.recurrenceEndDate) : '',
      });
      setShowModal(true);
      return;
    }

    setEditingEvent(raw);
    const isAllDay = raw.allDay || false;
    setForm({
      title: raw.title || '',
      description: raw.description || '',
      location: raw.location || '',
      startAt: raw.startAt ? (isAllDay ? toLocalDate(raw.startAt) : toLocalDatetime(raw.startAt)) : '',
      endAt: raw.endAt ? (isAllDay ? subtractDay(raw.endAt) : toLocalDatetime(raw.endAt)) : '',
      allDay: isAllDay,
      isPrivate: raw.isPrivate || false,
      categoryId: raw.categoryId || '',
      structureId: raw.structureId || '',
      recurrence: raw.recurrence || '',
      recurrenceEndDate: raw.recurrenceEndDate ? toLocalDate(raw.recurrenceEndDate) : '',
    });
    setShowModal(true);
  };

  // Drag & drop / resize
  const handleEventDrop = async (dropInfo) => {
    const e = dropInfo.event.extendedProps;
    // Ne pas déplacer les occurrences récurrentes ni les tâches
    if (e._originalId || e.type === 'task') {
      dropInfo.revert();
      toast.info('Les événements récurrents et tâches ne peuvent pas être déplacés');
      return;
    }
    try {
      await updateCalendarEvent(dropInfo.event.id, {
        startAt: dropInfo.event.startStr,
        endAt: dropInfo.event.endStr || null,
        allDay: dropInfo.event.allDay,
      });
      loadEvents();
    } catch {
      dropInfo.revert();
      toast.error('Erreur déplacement');
    }
  };

  // Sauvegarder
  const handleSave = async () => {
    if (!form.title.trim() || !form.startAt) {
      toast.error('Titre et date requis');
      return;
    }
    setSaving(true);
    // Convertir datetime-local en ISO avec timezone pour le backend
    // Pour les événements journée entière, FullCalendar attend end exclusif (+1 jour)
    let endAt = form.endAt ? new Date(form.endAt).toISOString() : null;
    if (form.allDay && form.endAt) {
      const endDate = new Date(form.endAt);
      endDate.setDate(endDate.getDate() + 1);
      endAt = endDate.toISOString();
    }
    const payload = {
      ...form,
      startAt: form.startAt ? new Date(form.startAt).toISOString() : null,
      endAt,
      recurrenceEndDate: form.recurrenceEndDate ? new Date(form.recurrenceEndDate).toISOString() : null,
    };
    try {
      if (exceptionInfo) {
        // Créer une exception d'occurrence récurrente
        await createCalendarException(exceptionInfo.originalId, {
          ...payload,
          excludeDate: exceptionInfo.excludeDate,
        });
        setExceptionInfo(null);
        toast.success('Occurrence modifiée');
      } else if (editingEvent) {
        await updateCalendarEvent(editingEvent.id, payload);
        toast.success('Événement modifié');
      } else {
        await createCalendarEvent(payload);
        toast.success('Événement créé');
      }
      setShowModal(false);
      loadEvents();
    } catch {
      toast.error('Erreur sauvegarde');
    }
    setSaving(false);
  };

  // Supprimer
  const handleDelete = async () => {
    if (!editingEvent) return;

    if (editingEvent.recurrence) {
      // Événement récurrent — 3 choix
      const choice = prompt(
        'Supprimer :\n1 = Toute la série\n2 = Cette occurrence et les suivantes\n\nTapez 1 ou 2 (Annuler pour ne rien faire)'
      );
      if (!choice) return;

      if (choice === '1') {
        // Supprimer toute la série
        if (!window.confirm('Supprimer TOUTE la série ? Cette action est irréversible.')) return;
        try {
          await deleteCalendarEvent(editingEvent.id);
          toast.success('Série supprimée');
        } catch { toast.error('Erreur suppression'); return; }
      } else if (choice === '2') {
        // Mettre recurrenceEndDate à la date de l'occurrence
        const endDate = editingEvent.startAt ? toLocalDate(editingEvent.startAt) : toLocalDate(new Date());
        try {
          await updateCalendarEvent(editingEvent.id, { recurrenceEndDate: new Date(endDate).toISOString() });
          toast.success('Occurrences suivantes supprimées');
        } catch { toast.error('Erreur modification'); return; }
      } else {
        return;
      }
    } else {
      if (!window.confirm('Supprimer cet événement ?')) return;
      try {
        await deleteCalendarEvent(editingEvent.id);
        toast.success('Événement supprimé');
      } catch { toast.error('Erreur suppression'); return; }
    }

    setShowModal(false);
    loadEvents();
  };

  // Terminer une tâche depuis le calendrier
  const handleCompleteTask = async (task) => {
    try {
      if (task._originalTaskId && task._occurrenceDate) {
        // Tâche récurrente → compléter uniquement cette occurrence
        await completeTaskOccurrence(task._originalTaskId, { completedDate: task._occurrenceDate });
        toast.success('Occurrence terminée');
      } else {
        // Tâche simple → marquer comme terminée
        await updateTask(task.id, { status: 'terminé' });
        toast.success('Tâche terminée');
      }
      setTaskPopup(null);
      loadEvents();
    } catch { toast.error('Erreur'); }
  };

  // Fermer le popup tâche au clic ailleurs
  useEffect(() => {
    if (!taskPopup) return;
    const handler = () => setTaskPopup(null);
    setTimeout(() => document.addEventListener('click', handler), 100);
    return () => document.removeEventListener('click', handler);
  }, [taskPopup]);

  const isMulti = showAllStructures && structures.length >= 2;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-indigo-100 rounded-xl">
            <Calendar size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Calendrier</h1>
            <p className="text-sm text-gray-500">{isMulti ? 'Toutes les structures' : (currentStructure?.name || '')}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* Bouton planning équipe — ouvre le drawer (tous breakpoints) */}
            <button
              onClick={() => setShowEmployeePanel(true)}
              className="relative flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
              title="Planning équipe"
            >
              <Users size={14} />
              <span className="hidden sm:inline">Équipe</span>
              {checkedEmployeeIds.size > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-semibold text-white bg-indigo-600 rounded-full">
                  {checkedEmployeeIds.size}
                </span>
              )}
            </button>
            <button onClick={() => {
              const userId = user?.sub || user?.id || '';
              const url = `${window.location.origin}/api/calendar/ical/${userId}`;
              navigator.clipboard.writeText(url);
              toast.success('Lien iCal copié ! Collez-le dans Google Calendar > Paramètres > Ajouter un agenda > À partir d\'une URL');
            }} className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm"
              title="Copier le lien iCal pour Google Calendar">
              <Link2 size={14} /> iCal
            </button>
            <button onClick={() => { setEditingEvent(null); setForm({ ...EMPTY_EVENT, structureId: '' }); setShowModal(true); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">
              <Plus size={15} /> Nouvel événement
            </button>
          </div>
        </div>

        {/* Calendrier pleine largeur (panneau employés en drawer accessible via bouton header) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          {loading && events.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="default" text="Chargement..." inline />
            </div>
          ) : (
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              locale="fr"
              firstDay={1}
              weekNumbers={true}
              weekNumberFormat={{ week: 'numeric' }}
              weekText="S"
              height="auto"
              events={[...events, ...planningEvents]}
              datesSet={(dateInfo) => {
                setCalendarRange({ start: dateInfo.start, end: dateInfo.end });
                loadEvents(dateInfo);
              }}
              selectable={true}
              editable={true}
              eventStartEditable={true}
              eventDurationEditable={true}
              select={handleDateSelect}
              eventClick={handleEventClick}
              eventDrop={handleEventDrop}
              eventResize={handleEventDrop}
              buttonText={{
                today: "Aujourd'hui",
                month: 'Mois',
                week: 'Semaine',
                day: 'Jour',
              }}
              dayMaxEvents={4}
              nowIndicator={true}
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
              eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
            />
          )}

          {/* Légende catégories */}
          {categories.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {categories.map(c => (
                <div key={c.id} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                  <span className="text-xs text-gray-500">{c.name}</span>
                </div>
              ))}
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#fde68a' }} />
                <span className="text-xs text-gray-500">Tâche</span>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Drawer Planning équipe — accessible sur tous breakpoints via bouton header */}
      {showEmployeePanel && (
        <div className="fixed inset-0 z-50 flex">
          {/* Overlay sombre pour fermer */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowEmployeePanel(false)}
          />
          {/* Panneau slide-in droite */}
          <div className="relative ml-auto w-72 max-w-[85vw] h-full bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Users size={14} className="text-gray-400" /> Planning équipe
                {checkedEmployeeIds.size > 0 && (
                  <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                    {checkedEmployeeIds.size}
                  </span>
                )}
              </h3>
              <button
                onClick={() => setShowEmployeePanel(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
                aria-label="Fermer"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <EmployeePanelContent
                employees={employees}
                employeeColors={employeeColors}
                checkedEmployeeIds={checkedEmployeeIds}
                toggleEmployee={toggleEmployee}
              />
            </div>
          </div>
        </div>
      )}

      {/* Popup tâche */}
      {taskPopup && (
        <div className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 w-72"
          style={{ left: Math.min(taskPopup.x, window.innerWidth - 300), top: Math.min(taskPopup.y, window.innerHeight - 200) }}
          onClick={e => e.stopPropagation()}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-1.5">
                <CheckSquare size={14} className={taskPopup.task.status === 'terminé' ? 'text-green-500' : 'text-amber-500'} />
                <h4 className="text-sm font-semibold text-gray-800">{taskPopup.task.title}</h4>
              </div>
              {taskPopup.task.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{taskPopup.task.description}</p>}
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                  taskPopup.task.status === 'terminé' ? 'bg-green-100 text-green-700' :
                  taskPopup.task.status === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-600'
                }`}>{taskPopup.task.status}</span>
                {taskPopup.task.assignedTo && <span>→ {taskPopup.task.assignedTo}</span>}
              </div>
            </div>
            <button onClick={() => setTaskPopup(null)} className="p-1 text-gray-400 hover:text-gray-600"><X size={14} /></button>
          </div>
          <div className="flex gap-2">
            {taskPopup.task.status !== 'terminé' && (
              <button onClick={() => handleCompleteTask(taskPopup.task)}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs hover:bg-green-700">
                <Check size={13} /> Terminer
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal création/édition */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setExceptionInfo(null); }} title={exceptionInfo ? 'Modifier cette occurrence' : editingEvent ? 'Modifier l\'événement' : 'Nouvel événement'} size="lg" preventClose={true}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Réunion d'équipe" autoFocus />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Début *</label>
              <input type={form.allDay ? 'date' : 'datetime-local'} value={form.startAt}
                onChange={e => setForm(f => ({ ...f, startAt: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fin</label>
              <input type={form.allDay ? 'date' : 'datetime-local'} value={form.endAt}
                onChange={e => setForm(f => ({ ...f, endAt: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.allDay} onChange={e => setForm(f => ({ ...f, allDay: e.target.checked }))} />
              Journée entière
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isPrivate} onChange={e => setForm(f => ({ ...f, isPrivate: e.target.checked }))} />
              <Lock size={13} className="text-gray-400" /> Privé (visible uniquement par moi)
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Récurrence</label>
            <select value={form.recurrence} onChange={e => setForm(f => ({ ...f, recurrence: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm">
              <option value="">Une seule fois</option>
              <option value="daily">Tous les jours</option>
              <option value="weekly">Toutes les semaines</option>
              <option value="biweekly">Toutes les 2 semaines</option>
              <option value="monthly">Tous les mois (même date)</option>
              {form.startAt && (() => {
                const d = new Date(form.startAt);
                const days = ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'];
                const nth = Math.ceil(d.getDate() / 7);
                const ordinals = ['','1er','2ème','3ème','4ème','5ème'];
                return <option value="monthly_weekday">{`Tous les mois (${ordinals[nth]} ${days[d.getDay()]})`}</option>;
              })()}
            </select>
            {form.recurrence && (
              <div className="mt-2">
                <label className="block text-xs text-gray-500 mb-1">Fin de récurrence (optionnel)</label>
                <input type="date" value={form.recurrenceEndDate}
                  onChange={e => setForm(f => ({ ...f, recurrenceEndDate: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
              <select value={form.categoryId} onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">— Aucune —</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Structure</label>
              <select value={form.structureId} onChange={e => setForm(f => ({ ...f, structureId: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Toutes</option>
                {structures.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Lieu</label>
            <input type="text" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Bureau, Choupi, Teams..." />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={3} placeholder="Détails..." />
          </div>

          <div className="flex justify-between pt-2">
            <div>
              {editingEvent && (
                <button onClick={handleDelete} className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg">
                  <Trash2 size={14} /> Supprimer
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={handleSave} disabled={saving || !form.title.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {saving ? <Loader size={14} className="animate-spin" /> : null}
                {editingEvent ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CalendarPage;
